import { t } from "i18next";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { showApiErrorToast } from "../../services/api/error";
import { Typography } from "@material-tailwind/react";
import { HomeIcon, ClipboardDocumentCheckIcon } from "@heroicons/react/24/solid";
/***/

/* Services */
import { api } from "../../services/api/api";
/***/

/* Components */
import { Breadcrumb } from "../../components/breadcrumb/Breadcrumb";
import Loading from "../../components/loading/Loading";
import { TabsNav } from "../../components/tabs/TabsNav";
import { 
  InformationsTab, 
  InterventionsTab 
} from "./tabs";
/***/

/* Styles */
import "./Machine.scss";
/***/

/**
* Machine: machine details view
* display details informations about machine
*/
export function Machine() {
  const { customerId, id } = useParams();
  const [machine, setMachine] = useState<EasyTech.Machine>();
  const [customer, setCustomer] = useState<EasyTech.Customer>();
  const [path, setPath] = useState<{name: string, path: string}[]>([]);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("informations");

  /**
  * useEffect: Get machine data
  * @param id - reload on machine id change 
  */
  useEffect(() => {
    if (id) {
      api.get(`users/machines/${id}`)
        .then((ret) => setMachine(ret as EasyTech.Machine))
        .catch((err) => {
          showApiErrorToast(err);
          navigate("/not-found");
        });
    }
  }, [id, navigate]);
  /***/

  /**
  * useEffect: Get customer data for breadcrumb
  * @param customerId - reload on customer id change 
  */
  useEffect(() => {
    if (customerId) {
      api.get(`users/customers/${customerId}`)
        .then((ret) => setCustomer(ret as EasyTech.Customer))
        .catch((err) => {
          console.error("Error fetching customer:", err);
        });
    }
  }, [customerId]);
  /***/

  /**
  * useEffect: Set breadcrumb
  * @param machine, customer - reload on machine or customer change
  */
  useEffect(() => { 
    if (machine && customer) {
      setPath([{
        name: "customers",
        path: "/customers"
      }, {
        name: `${customer.name} - ${customer.ref}`,
        path: `/customers/${customer.id}`
      }, {
        name: `Machine - ${machine.serialNumber}`,
        path: `/customers/${customer.id}/machines/${machine.id}`
      }]);
    }
  }, [machine, customer]);
  /***/

  /**
   * handleTabChange: Handle tab change
   * @param value - new tab
   */
  const handleTabChange = (tabValue: string) => {
    setActiveTab(tabValue);
  };

  const tabs = [
    {
      label: t("machine:tabs:informations"),
      value: "informations",
      icon: HomeIcon,
      Component: (props: { data: EasyTech.Machine }) => (
        <InformationsTab data={props.data} />
      ),
      disabled: false,
    },
    {
      label: t("machine:tabs:interventions"),
      value: "interventions",
      icon: ClipboardDocumentCheckIcon,
      Component: InterventionsTab,
      disabled: false,
    },
  ];

  return (
    <div className="machine-details">
      {machine && customer ?
        <>
          <Breadcrumb path={path}/>

          <Typography variant="h2">
            {machine.brand?.label} - {machine.type?.label} ({machine.serialNumber})
          </Typography>

          <TabsNav
            defaultTab={activeTab}
            data={machine}
            tabs={tabs}
            onTabChange={handleTabChange}
          />
        </>
        :
        <Loading/>}
    </div>
  );
}
/***/ 